import { convertCompatFormatter, EventEmitter, Group, GroupItemMetadataProvider, GroupTotals, IGroupTotals, ISleekGrid, ItemMetadata, type FormatterContext, type NonDataRow } from "@serenity-is/sleekgrid";
import { ListRequest, ListResponse, PagerTexts, serviceCall, ServiceOptions, ServiceResponse } from "../base";
import { AggregateFormatting } from "./aggregateformatting";
import { IAggregator } from "./aggregators";
import { CancellableViewCallback, IRemoteView, PagingInfo, RemoteViewAjaxCallback, RemoteViewFilter, RemoteViewProcessCallback } from "./iremoteview";
import { GroupInfo, PagingOptions, SummaryOptions } from "./slicktypes";

export interface ArgsRemoteView {
    dataView: IRemoteView;
}

export interface ArgsGroupToggle extends ArgsRemoteView {
    groupingKey: string;
    level: number;
}

export interface ArgsPagingInfo extends ArgsRemoteView {
    pagingInfo: PagingInfo;
}

export interface ArgsRowCountChanged extends ArgsRemoteView {
    previous: number;
    current: number;
}

export interface ArgsRowsChanged extends ArgsRemoteView {
    rows: number[];
}

export interface ArgsRowsOrCountChanged extends ArgsRemoteView {
    rowsDiff: number[];
    previousRowCount: number;
    currentRowCount: number;
    rowCountChanged: boolean;
    rowsChanged: boolean;
}

export interface ArgsRecalcRows extends ArgsRemoteView {
    oldRows: any[];
    newRows: any[];
}

/**
 * A data view that supports remote data loading, sorting, filtering, grouping, and paging.
 * Extends the functionality of SleekGrid's DataView with server-side data operations.
 */
export class RemoteView<TItem = any> implements IRemoteView<TItem> {
    private contentType: string;
    private dataType: string;
    private errormsg: string;
    private errorMessage: string = null;
    private filter: RemoteViewFilter<TItem>;
    private filterCache: any[] = [];
    private filteredItems: TItem[] = [];
    private grandAggregators: IAggregator[];
    private grandTotals: IGroupTotals;
    private groupingInfos: GroupInfo<TItem>[] = [];
    private groupItemMetadataProvider: GroupItemMetadataProvider;
    private groups: Group<TItem>[] = [];
    private idProperty: string;
    private idxById: Record<any, number> = {};
    private itemMetadataCallback?: (item?: TItem, row?: number) => ItemMetadata<TItem>;
    private items: TItem[] = [];
    private loading: AbortController | boolean = false;
    private localSort: boolean;
    private method: string;
    private page = 1;
    private populateCalls = 0;
    private populateLocks = 0;
    private prevRefreshHints: any = {};
    private refreshHints: any = {};
    private rows: (TItem | Group | GroupTotals)[] = [];
    private rowsById: Record<any, number> = null;
    private rowsPerPage: number;
    private sortAsc = true;
    private sortComparer: any;
    private suspend = 0;
    private toggledGroupsByLevel: any[] = [];
    private totalCount: number = null;
    private totalRows = 0;
    private updated: Record<string, boolean> = null;
    
    public params: Record<string, any>;
    public seekToPage: number;
    public sortBy: string[];
    public url: string;

    public onAjaxCall: RemoteViewAjaxCallback<TItem>;
    public onProcessData: RemoteViewProcessCallback<TItem>;
    public onSubmit: CancellableViewCallback<TItem>;
    public readonly onDataChanged = new EventEmitter<ArgsRemoteView>();
    public readonly onDataLoaded = new EventEmitter<ArgsRemoteView>();
    public readonly onDataLoading = new EventEmitter<ArgsRemoteView>();
    public readonly onGroupCollapsed = new EventEmitter<ArgsGroupToggle>();
    public readonly onGroupExpanded = new EventEmitter<ArgsGroupToggle>();
    public readonly onPagingInfoChanged = new EventEmitter<ArgsPagingInfo>();
    public readonly onRecalcRows = new EventEmitter<ArgsRecalcRows>();
    public readonly onRowCountChanged = new EventEmitter<ArgsRowCountChanged>();
    public readonly onRowsChanged = new EventEmitter<ArgsRowsChanged>();
    public readonly onRowsOrCountChanged = new EventEmitter<ArgsRowsOrCountChanged>();

    constructor(options: RemoteViewOptions<TItem>) {
        options ??= {}
        AggregateFormatting.initGridDefaults();
        this.groupItemMetadataProvider = options.groupItemMetadataProvider;
        this.localSort = options.localSort ?? false;

        this.idProperty = options.idField ?? 'id';
        this.contentType = options.contentType ?? "application/json";
        this.dataType = options.dataType ?? 'json';
        this.filter = options.filter ?? null;
        this.itemMetadataCallback = options.getItemMetadata;
        this.params = options.params ?? {};
        this.onSubmit = options.onSubmit ?? null;
        this.url = options.url || null;
        this.rowsPerPage = options.rowsPerPage || 0;
        this.seekToPage = options.seekToPage || 1;
        this.onAjaxCall = options.onAjaxCall || null;
        this.onProcessData = options.onProcessData || null;
        this.method = options.method || "POST";
        this.errormsg = options.errormsg;
        this.sortBy = typeof options.sortBy == "string" && options.sortBy !== "" ? [options.sortBy] : (options.sortBy || []);

        options.url && options.autoLoad && this.populate();
    }

    /** Default configuration for grouping information */
    static readonly groupingInfoDefaults: GroupInfo<any> = {
        comparer: function (this: void, a: any, b: any) {
            return (a.value === b.value ? 0 :
                (a.value > b.value ? 1 : -1)
            );
        },
        aggregateEmpty: false,
        aggregateCollapsed: false,
        aggregateChildGroups: false,
        collapsed: false,
        displayTotalsRow: true,
        lazyTotalsCalculation: false,
        predefinedValues: []
    }

    public beginUpdate() {
        this.suspend++;
    }

    public endUpdate() {
        this.suspend--;
        if (this.suspend <= 0)
            this.refresh();
    }

    /**
     * Sets hints for the next refresh operation to optimize performance.
     * @param hints Object containing refresh hints like isFilterNarrowing, isFilterExpanding, etc.
     */
    public setRefreshHints(hints: any) {
        this.refreshHints = hints;
    }

    private updateIdxById(startingIndex?: number) {
        startingIndex = startingIndex || 0;
        let id: any;
        for (let i = startingIndex, l = this.items.length; i < l; i++) {
            id = (this.items[i] as any)[this.idProperty];
            if (id === undefined) {
                throw (`Each data element must implement a unique '${this.idProperty}' property. ` +
                    `Object at index '${i}' has no identity value: ${JSON.stringify(this.items[i])}`);
            }
            this.idxById[id] = i;
        }
    }

    private ensureIdUniqueness(): void {
        let id: any;
        for (let i = 0, l = this.items.length; i < l; i++) {
            id = (this.items[i] as any)[this.idProperty];
            if (id === undefined || this.idxById[id] !== i) {
                throw (`Each data element must implement a unique '${this.idProperty}' property. Object at index '${i}' ` +
                    (id == undefined ? "has no identity value: " : "has repeated identity value '" + id + "': ") +
                    JSON.stringify(this.items[i]));
            }
        }
    }

    public getItems(): TItem[] {
        return this.items;
    }

    public getIdPropertyName(): string {
        return this.idProperty;
    }

    public setItems(data: any[], newIdProperty?: string | boolean): void {
        if (newIdProperty != null && typeof newIdProperty == "string")
            this.idProperty = newIdProperty;

        this.items = this.filteredItems = data;
        if (this.localSort) {
            this.items.sort(this.getSortComparer());
        }

        this.idxById = {};
        this.rowsById = null;
        this.grandTotals = {};
        this.updateIdxById();
        this.ensureIdUniqueness();

        if (this.suspend) {
            this.recalc(this.items);
        }
        else {
            this.refresh();
        }

        this.onDataChanged.notify({ dataView: this }, null, this);
    }

    public setPagingOptions(args: PagingOptions): void {
        let anyChange = false;

        if (args.rowsPerPage != undefined &&
            this.rowsPerPage != args.rowsPerPage) {
            this.rowsPerPage = args.rowsPerPage;
            anyChange = true;
        }

        if (args.page != undefined) {
            let newPage: number;
            if (!this.rowsPerPage)
                newPage = 1;
            else if (this.totalCount == null)
                newPage = args.page;
            else
                newPage = Math.min(args.page, Math.ceil(this.totalCount / this.rowsPerPage) + 1);

            if (newPage < 1)
                newPage = 1;

            if (newPage != this.page) {
                this.seekToPage = newPage;
                anyChange = true;
            }
        }

        if (anyChange)
            this.populate();
    }

    public getPagingInfo(): PagingInfo {
        return {
            rowsPerPage: this.rowsPerPage,
            page: this.page,
            totalCount: this.totalCount,
            loading: this.loading != null && this.loading != false,
            error: this.errorMessage,
            dataView: this
        };
    }

    private getSortComparer(): (a: any, b: any) => number {
        if (this.sortComparer != null)
            return this.sortComparer;

        const cols: string[] = [];
        const asc: boolean[] = [];
        const sorts = this.sortBy || [];
        for (const s of sorts) {
            if (s == null)
                continue;
            if (s.length > 5 && s.toLowerCase().substring(s.length - 5).toLowerCase() == ' desc') {
                asc.push(false);
                cols.push(s.substring(0, s.length - 5));
            }
            else {
                asc.push(true);
                cols.push(s);
            }
        }

        return function (this: void, a: any, b: any) {
            for (let i = 0, l = cols.length; i < l; i++) {
                const field = cols[i];
                const sign = asc[i] ? 1 : -1;
                const value1 = a[field], value2 = b[field];
                const result = (value1 == value2 ? 0 : (value1 > value2 ? 1 : -1)) * sign;
                if (result != 0) {
                    return result;
                }
            }
            return 0;
        }
    }

    public sort(comparer?: (a: any, b: any) => number, ascending?: boolean): void {
        this.sortAsc = ascending;
        if (ascending === false) {
            this.items.reverse();
        }

        this.sortComparer = comparer;
        this.items.sort(this.getSortComparer());

        if (ascending === false) {
            this.items.reverse();
        }
        this.idxById = {};
        this.updateIdxById();
        this.refresh();
    }

    public getLocalSort(): boolean {
        return this.localSort;
    }

    public setLocalSort(value: boolean): void {
        if (this.localSort != value) {
            this.localSort = value;
            this.sort();
        }
    }

    public reSort() {
        this.sort(this.sortComparer, this.sortAsc);
    }

    public getFilteredItems(): TItem[] {
        return this.filteredItems;
    }

    public getFilter() {
        return this.filter;
    }

    public setFilter(filterFn: RemoteViewFilter<TItem>): void {
        this.filter = filterFn;
        this.refresh();
    }

    public getGrouping() {
        return this.groupingInfos;
    }

    public setSummaryOptions(summary: SummaryOptions): void {
        this.grandAggregators = summary?.aggregators || [];
        this.grandTotals = {};
        this.setGrouping(this.groupingInfos || []);
    }

    public getGrandTotals(): IGroupTotals {
        this.grandTotals ??= {};

        if (!this.grandTotals.initialized) {
            this.grandAggregators ??= [];
            let agg: IAggregator, idx = this.grandAggregators.length;

            while (idx--) {
                agg = this.grandAggregators[idx];
                agg.init();
                for (const item of this.items)
                    agg.accumulate(item);
                agg.storeResult(this.grandTotals);
            }
            this.grandTotals.initialized = true;
        }

        return this.grandTotals;
    }

    public setGrouping(groupingInfo: GroupInfo<TItem> | GroupInfo<TItem>[]): void {
        if (!this.groupItemMetadataProvider) {
            this.groupItemMetadataProvider = new GroupItemMetadataProvider();
        }

        this.groups = [];
        this.toggledGroupsByLevel = [];
        groupingInfo = groupingInfo || [];
        this.groupingInfos = (groupingInfo instanceof Array) ? groupingInfo : [groupingInfo];

        for (let i = 0; i < this.groupingInfos.length; i++) {
            const gi = this.groupingInfos[i] = Object.assign({}, RemoteView.groupingInfoDefaults, this.groupingInfos[i]);
            gi.getterIsAFn ??= typeof gi.getter == "function";
            this.toggledGroupsByLevel[i] = {};
        }

        this.refresh();
    }

    public getItemByIdx(i: number): any {
        return this.items[i];
    }

    public getIdxById(id: any): number {
        return this.idxById[id];
    }

    private ensureRowsByIdCache(): void {
        if (!this.rowsById) {
            this.rowsById = {};
            for (let i = 0, l = this.rows.length; i < l; i++) {
                this.rowsById[(this.rows[i] as any)[this.idProperty]] = i;
            }
        }
    }

    public getRowByItem(item: any): number {
        this.ensureRowsByIdCache();
        return this.rowsById[item[this.idProperty]];
    }

    public getRowById(id: any): number {
        this.ensureRowsByIdCache();
        return this.rowsById[id];
    }

    public getItemById(id: any): TItem {
        return this.items[this.idxById[id]];
    }

    /**
     * Maps an array of items to their corresponding row indices.
     * @param itemArray Array of items to map
     * @returns Array of row indices
     */
    public mapItemsToRows(itemArray: any[]) {
        const rows = [];
        this.ensureRowsByIdCache();
        for (let i = 0, l = itemArray.length; i < l; i++) {
            const row = this.rowsById[itemArray[i][this.idProperty]];
            if (row != null) {
                rows[rows.length] = row;
            }
        }
        return rows;
    }

    /**
     * Maps an array of IDs to their corresponding row indices.
     * @param idArray Array of item IDs to map
     * @returns Array of row indices
     */
    public mapIdsToRows(idArray: any[]) {
        const rows: any[] = [];
        this.ensureRowsByIdCache();
        for (let i = 0, l = idArray.length; i < l; i++) {
            const row = this.rowsById[idArray[i]];
            if (row != null) {
                rows[rows.length] = row;
            }
        }
        return rows;
    }

    /**
     * Maps an array of row indices to their corresponding item IDs.
     * @param rowArray Array of row indices to map
     * @returns Array of item IDs
     */
    public mapRowsToIds(rowArray: any[]) {
        const ids: any[] = [];
        for (let i = 0, l = rowArray.length; i < l; i++) {
            if (rowArray[i] < this.rows.length) {
                ids[ids.length] = (this.rows[rowArray[i]] as any)[this.idProperty];
            }
        }
        return ids;
    }

    public updateItem(id: any, item: any) {
        if (this.idxById[id] === undefined) {
            throw new Error("Invalid id");
        }

        if (id !== item[this.idProperty]) {
            // make sure the new id is unique:
            const newId = item[this.idProperty];
            if (newId == null) {
                throw new Error("Cannot update item to associate with a null id");
            }
            if (this.idxById[newId] !== undefined) {
                throw new Error("Cannot update item to associate with a non-unique id");
            }
            this.idxById[newId] = this.idxById[id];
            delete this.idxById[id];

            if (this.updated && this.updated[id]) {
                delete this.updated[id];
            }

            id = newId;
        }
        this.items[this.idxById[id]] = item;

        if (!this.updated) {
            this.updated = {};
        }
        this.updated[id] = true;
        this.refresh();
    }

    public insertItem(insertBefore: number, item: any) {
        this.items.splice(insertBefore, 0, item);
        this.updateIdxById(insertBefore);
        this.refresh();
    }

    public addItem(item: any) {
        this.items.push(item);
        this.updateIdxById(this.items.length - 1);
        this.refresh();
    }

    public deleteItem(id: any) {
        const idx = this.idxById[id];
        if (idx === undefined) {
            throw "Invalid id";
        }
        delete this.idxById[id];
        this.items.splice(idx, 1);
        this.updateIdxById(idx);
        this.refresh();
    }

    public sortedAddItem(item: any) {
        this.insertItem(this.sortedIndex(item), item);
    }

    public sortedUpdateItem(id: any, item: any) {
        if (this.idxById[id] === undefined || id !== item[this.idProperty]) {
            throw new Error("Invalid or non-matching id " + this.idxById[id]);
        }
        const comparer = this.getSortComparer();
        const oldItem = this.getItemById(id);
        if (comparer(oldItem, item) !== 0) {
            // item affects sorting -> must use sorted add
            this.deleteItem(id);
            this.sortedAddItem(item);
        }
        else { // update does not affect sorting -> regular update works fine
            this.updateItem(id, item);
        }
    }

    private sortedIndex(searchItem: any) {
        let low = 0, high = this.items.length;
        const comparer = this.getSortComparer();
        while (low < high) {
            const mid = low + high >>> 1;
            if (comparer(this.items[mid], searchItem) === -1) {
                low = mid + 1;
            }
            else {
                high = mid;
            }
        }
        return low;
    }

    public getRows(): (TItem | Group<any> | GroupTotals<any>)[] {
        return this.rows;
    }

    public getLength() {
        return this.rows.length;
    }

    public getItem(i: number): any {
        const item = this.rows[i];

        const group = item as Group;
        // if this is a group row, make sure the group is collapsed/expanded as needed
        // if this is a group row, make sure totals are calculated and update the title
        if (group && group.__group && group.totals && !group.totals.initialized) {
            const gi = this.groupingInfos[group.level];
            if (!gi.displayTotalsRow) {
                this.calculateTotals(group.totals);
                group.formatValue = this.formatGroupValue;
            }
        }
        // if this is a totals row, make sure it's calculated
        else if (item && (item as IGroupTotals).__groupTotals && !(item as IGroupTotals).initialized) {
            this.calculateTotals(item);
        }

        return item;
    }

    public getItemMetadata(row: number): ItemMetadata<TItem> {
        const item = this.rows[row];
        if (item === undefined) {
            return null;
        }

        // overrides for grouping rows
        if ((item as Group).__group) {
            return this.groupItemMetadataProvider.getGroupRowMetadata(item as Group);
        }

        // overrides for totals rows
        if ((item as IGroupTotals).__groupTotals) {
            return this.groupItemMetadataProvider.getTotalsRowMetadata(item);
        }

        return (this.itemMetadataCallback && this.itemMetadataCallback(item as TItem, row)) || null;
    }

    private expandCollapseAllGroups(level: number, collapse: boolean) {
        if (level == null) {
            for (let i = 0; i < this.groupingInfos.length; i++) {
                this.toggledGroupsByLevel[i] = {};
                this.groupingInfos[i].collapsed = collapse;

                if (collapse === true) {
                    this.onGroupCollapsed.notify({ level: i, groupingKey: null, dataView: this }, null, this);
                } else {
                    this.onGroupExpanded.notify({ level: i, groupingKey: null, dataView: this }, null, this);
                }
            }
        } else {
            this.toggledGroupsByLevel[level] = {};
            this.groupingInfos[level].collapsed = collapse;

            if (collapse === true) {
                this.onGroupCollapsed.notify({ level: level, groupingKey: null, dataView: this }, null, this);
            } else {
                this.onGroupExpanded.notify({ level: level, groupingKey: null, dataView: this }, null, this);
            }
        }
        this.refresh();
    }

    public collapseAllGroups(level?: number) {
        this.expandCollapseAllGroups(level, true);
    }

    public expandAllGroups(level?: number) {
        this.expandCollapseAllGroups(level, false);
    }

    private resolveLevelAndGroupingKey(args: any) {
        const arg0 = args[0];
        if (args.length === 1 && arg0.indexOf(groupingDelimiter) !== -1) {
            return { level: arg0.split(groupingDelimiter).length - 1, groupingKey: arg0 };
        } else {
            return { level: args.length - 1, groupingKey: args.join(groupingDelimiter) };
        }
    }

    private expandCollapseGroup(args: any, collapse: any) {
        const opts = this.resolveLevelAndGroupingKey(args);
        this.toggledGroupsByLevel[opts.level][opts.groupingKey] = !this.groupingInfos[opts.level].collapsed !== !collapse;
        if (collapse)
            this.onGroupCollapsed.notify({ level: opts.level, groupingKey: opts.groupingKey, dataView: this }, null, this);
        else
            this.onGroupExpanded.notify({ level: opts.level, groupingKey: opts.groupingKey, dataView: this }, null, this);

        this.refresh();
    }

    public collapseGroup(constArgs: any[]) {
        const args = Array.prototype.slice.call(arguments);
        this.expandCollapseGroup(args, true);
    }

    public expandGroup(constArgs: any[]) {
        const args = Array.prototype.slice.call(arguments);
        this.expandCollapseGroup(args, false);
    }

    public getGroups() {
        return this.groups;
    }

    private getOrCreateGroup(groupsByVal: Record<any, Group<TItem>>, val: any, level: number, parentGroup: any, groups: Group<TItem>[]) {
        let group = groupsByVal[val];

        if (!group) {
            group = new Group<TItem>();
            group.value = val;
            group.level = level;
            group.groupingKey = (parentGroup ? parentGroup.groupingKey + groupingDelimiter : '') + val;
            groups[groups.length] = group;
            groupsByVal[val] = group;
        }

        return group;
    }

    private extractGroups(items: TItem[], parentGroup?: Group<TItem>): Group<TItem>[] {
        let group: Group<TItem>;
        let val: any;
        const groups: Group<TItem>[] = [];
        const groupsByVal: Record<any, Group<TItem>> = {};
        let item: TItem;
        const level = parentGroup ? parentGroup.level + 1 : 0;
        const groupingInfo = this.groupingInfos[level];

        for (let i1 = 0, l: number = groupingInfo.predefinedValues.length; i1 < l; i1++) {
            val = groupingInfo.predefinedValues[i1];
            group = this.getOrCreateGroup(groupsByVal, val, level, parentGroup, groups);
        }

        for (let i2 = 0, l = items.length; i2 < l; i2++) {
            item = items[i2];
            val = groupingInfo.getterIsAFn ? (groupingInfo.getter as any)(item) : (item as any)[groupingInfo.getter as string];
            group = this.getOrCreateGroup(groupsByVal, val, level, parentGroup, groups);

            group.rows[group.count++] = item;
        }

        if (level < this.groupingInfos.length - 1) {
            for (let i3 = 0; i3 < groups.length; i3++) {
                group = groups[i3];
                group.groups = this.extractGroups(group.rows, group);
            }
        }

        if (groups.length) {
            this.addTotals(groups, level);
        }

        groups.sort(this.groupingInfos[level].comparer);

        return groups;
    }

    private calculateTotals(totals: IGroupTotals<TItem>) {
        const group = totals.group;
        const gi = this.groupingInfos[group.level];
        const isLeafLevel = (group.level == this.groupingInfos.length);
        const aggregators = (gi.aggregators ?? this.grandAggregators ?? []);
        let agg: IAggregator, idx = aggregators.length;

        if (!isLeafLevel && gi.aggregateChildGroups) {
            // make sure all the subgroups are calculated
            let i = group.groups.length;
            while (i--) {
                if (!group.groups[i].totals.initialized) {
                    this.calculateTotals(group.groups[i].totals);
                }
            }
        }

        while (idx--) {
            agg = aggregators[idx];
            agg.init();
            const items = !isLeafLevel && gi.aggregateChildGroups ? group.groups : group.rows;
            for (const item of items) {
                agg.accumulate(item);
            }
            agg.storeResult(totals);
        }
        totals.initialized = true;
    }

    private addGroupTotals(group: Group<TItem>) {
        const gi = this.groupingInfos[group.level];
        const totals = new GroupTotals<TItem>();
        totals.group = group;
        group.totals = totals;
        if (!gi.lazyTotalsCalculation) {
            this.calculateTotals(totals);
        }
    }

    private addTotals(groups: Group<TItem>[], level?: number) {
        level = level || 0;
        const gi = this.groupingInfos[level];
        const aggregators = (gi?.aggregators ?? this.grandAggregators ?? []);
        const groupCollapsed = !!gi?.collapsed;
        const toggledGroups = this.toggledGroupsByLevel[level];
        let idx = groups.length, g: Group<TItem>;
        while (idx--) {
            g = groups[idx];

            if (g.collapsed && !gi?.aggregateCollapsed) {
                continue;
            }

            // Do a depth-first aggregation so that parent group aggregators can access subgroup totals.
            if (g.groups) {
                this.addTotals(g.groups, level + 1);
            }

            if (aggregators.length && (
                gi.aggregateEmpty || g.rows.length || (g.groups && g.groups.length))) {
                this.addGroupTotals(g);
            }

            g.collapsed = groupCollapsed !== !!toggledGroups[g.groupingKey];
            g.formatValue = this.formatGroupValue;
        }
    }

    private flattenGroupedRows(groups: Group<TItem>[], level?: number) {
        level = level || 0;
        const gi = this.groupingInfos[level];
        const groupedRows: any[] = [];
        let rows: any[], gl = 0, g: Group<TItem>;
        for (let i = 0, l = groups.length; i < l; i++) {
            g = groups[i];
            groupedRows[gl++] = g;

            if (!g.collapsed) {
                rows = g.groups ? this.flattenGroupedRows(g.groups, level + 1) : g.rows;
                for (let j = 0, jj = rows.length; j < jj; j++) {
                    groupedRows[gl++] = rows[j];
                }
            }

            if (g.totals && gi.displayTotalsRow && (!g.collapsed || gi.aggregateCollapsed)) {
                groupedRows[gl++] = g.totals;
            }
        }
        return groupedRows;
    }

    private batchFilter(items: TItem[]) {
        const retval: TItem[] = [];
        let idx = 0;

        for (let i = 0, ii = items.length; i < ii; i++) {
            if (this.filter(items[i], this)) {
                retval[idx++] = items[i];
            }
        }

        return retval;
    }

    private batchFilterWithCaching(items: TItem[], cache: any) {
        const retval: TItem[] = [];
        let idx = 0, item: TItem;

        for (let i = 0, ii = items.length; i < ii; i++) {
            item = items[i];
            if (cache[i]) {
                retval[idx++] = item;
            } else if (this.filter(item, this)) {
                retval[idx++] = item;
                cache[i] = true;
            }
        }

        return retval;
    }

    private getFilteredAndPagedItems(items: TItem[]) {
        if (this.filter) {
            if (this.refreshHints?.isFilterNarrowing) {
                this.filteredItems = this.batchFilter(this.filteredItems);
            } else if (this.refreshHints?.isFilterExpanding) {
                this.filteredItems = this.batchFilterWithCaching(items, this.filterCache);
            } else if (!this.refreshHints?.isFilterUnchanged) {
                this.filteredItems = this.batchFilter(items);
            }
        } else {
            // special case:  if not filtering and not paging, the resulting
            // rows collection needs to be a copy so that changes due to sort
            // can be caught
            this.filteredItems = items.concat();
        }

        // get the current page
        return { totalRows: this.filteredItems.length, rows: this.filteredItems };
    }

    private getRowDiffs(rows: (TItem | Group | GroupTotals)[], newRows: (TItem | Group | GroupTotals)[]): number[] {
        let item: TItem | Group | GroupTotals, r: TItem | Group | GroupTotals, eitherIsNonData: boolean, diff: number[] = [];
        let from = 0, to = newRows.length;

        if (this.refreshHints?.ignoreDiffsBefore) {
            from = Math.max(0,
                Math.min(newRows.length, this.refreshHints.ignoreDiffsBefore));
        }

        if (this.refreshHints?.ignoreDiffsAfter) {
            to = Math.min(newRows.length,
                Math.max(0, this.refreshHints.ignoreDiffsAfter));
        }

        for (let i = from, rl = rows.length; i < to; i++) {
            if (i >= rl) {
                diff[diff.length] = i;
            } else {
                item = newRows[i];
                r = rows[i];

                if ((this.groupingInfos.length && (eitherIsNonData = (item as NonDataRow).__nonDataRow || (r as NonDataRow).__nonDataRow) &&
                    (item as Group).__group !== (r as Group).__group ||
                    (item as Group).__group && !(item as Group).equals(r as Group))
                    || (eitherIsNonData &&
                        // no good way to compare totals since they are arbitrary DTOs
                        // deep object comparison is pretty expensive
                        // always considering them 'dirty' seems easier for the time being
                        ((item as GroupTotals).__groupTotals || (r as GroupTotals).__groupTotals))
                    || (item as any)[this.idProperty] != (r as any)[this.idProperty]
                    || (this.updated && this.updated[(item as any)[this.idProperty]])
                ) {
                    diff[diff.length] = i;
                }
            }
        }
        return diff;
    }

    private recalc(items: TItem[]): number[] {
        this.rowsById = null;

        if (this.refreshHints?.isFilterNarrowing != this.prevRefreshHints?.isFilterNarrowing ||
            this.refreshHints?.isFilterExpanding != this.prevRefreshHints?.isFilterExpanding) {
            this.filterCache = [];
        }

        const filteredItems = this.getFilteredAndPagedItems(items);
        this.totalRows = filteredItems.totalRows;
        let newRows = filteredItems.rows;

        this.grandTotals = {};

        this.groups = [];
        if (this.groupingInfos.length) {
            this.groups = this.extractGroups(newRows);
            if (this.groups.length) {
                newRows = this.flattenGroupedRows(this.groups);
            }
        }

        let oldRows = this.rows || [];
        const e = { dataView: this, oldRows: oldRows, newRows: newRows };
        this.onRecalcRows.notify(e, null, this);
        oldRows = e.oldRows || oldRows;
        newRows = e.newRows || newRows;

        const diff = this.getRowDiffs(oldRows, newRows);

        this.rows = newRows;

        return diff;
    }

    public refresh() {
        if (this.suspend) {
            return;
        }

        const countBefore = this.rows.length;
        const totalRowsBefore = this.totalRows;

        const diff = this.recalc(this.items); // pass as direct refs to avoid closure perf hit

        this.updated = null;
        this.prevRefreshHints = this.refreshHints;
        this.refreshHints = {};

        if (totalRowsBefore !== this.totalRows) {
            this.onPagingInfoChanged.notify({ pagingInfo: this.getPagingInfo(), dataView: this }, null, this);
        }
        if (countBefore !== this.rows.length) {
            this.onRowCountChanged.notify({ previous: countBefore, current: this.rows.length, dataView: this }, null, this);
        }
        if (diff.length > 0) {
            this.onRowsChanged.notify({ rows: diff, dataView: this }, null, this);
        }
        if (countBefore !== this.rows.length || diff.length > 0) {
            this.onRowsOrCountChanged.notify({
                rowsDiff: diff,
                previousRowCount: countBefore,
                currentRowCount: this.rows.length,
                rowCountChanged: countBefore !== this.rows.length,
                rowsChanged: diff.length > 0,
                dataView: this
            }, null, this);
        }
    }

    /***
     * Wires the grid and the DataView together to keep row selection tied to item ids.
     * This is useful since, without it, the grid only knows about rows, so if the items
     * move around, the same rows stay selected instead of the selection moving along
     * with the items.
     *
     * NOTE:  This doesn't work with cell selection model.
     *
     * @param sleekGrid The grid to sync selection with.
     * @param preserveHidden Whether to keep selected items that go out of the
     *     view due to them getting filtered out.
     * @param preserveHiddenOnSelectionChange Whether to keep selected items
     *     that are currently out of the view (see preserveHidden) as selected when selection
     *     changes.
     * @return An event that notifies when an internal list of selected row ids
     *     changes.  This is useful since, in combination with the above two options, it allows
     *     access to the full list selected row ids, and not just the ones visible to the grid.
     */
    public syncGridSelection(sleekGrid: ISleekGrid, preserveHidden?: boolean, preserveHiddenOnSelectionChange?: boolean): EventEmitter<any> {
        let inHandler: boolean;;
        let selectedRowIds = this.mapRowsToIds(sleekGrid.getSelectedRows());
        const onSelectedRowIdsChanged = new EventEmitter();

        const self = this;
        function setSelectedRowIds(this: void, rowIds: any[]) {
            if (selectedRowIds.join(",") == rowIds.join(",")) {
                return;
            }

            selectedRowIds = rowIds;

            onSelectedRowIdsChanged.notify({
                "grid": sleekGrid,
                "ids": selectedRowIds,
                "dataView": this
            }, null, self);
        }

        function update(this: void) {
            if (selectedRowIds.length > 0) {
                inHandler = true;
                const selectedRows = self.mapIdsToRows(selectedRowIds);
                if (!preserveHidden) {
                    setSelectedRowIds(self.mapRowsToIds(selectedRows));
                }
                sleekGrid.setSelectedRows(selectedRows);
                inHandler = false;
            }
        }

        sleekGrid.onSelectedRowsChanged.subscribe(function (this: void) {
            if (inHandler) { return; }
            const newSelectedRowIds = self.mapRowsToIds(sleekGrid.getSelectedRows());
            if (!preserveHiddenOnSelectionChange || !sleekGrid.getOptions().multiSelect) {
                setSelectedRowIds(newSelectedRowIds);
            } else {
                // keep the ones that are hidden
                const existing = selectedRowIds.filter((id: any) => self.getRowById(id) === undefined);
                // add the newly selected ones
                setSelectedRowIds(existing.concat(newSelectedRowIds));
            }
        });

        this.onRowsChanged.subscribe(update);
        this.onRowCountChanged.subscribe(update);

        return onSelectedRowIdsChanged;
    }

    public syncGridCellCssStyles(grid: ISleekGrid, key: string) {
        let hashById: any;
        let inHandler: boolean;

        // since this method can be called after the cell styles have been set,
        // get the existing ones right away
        storeCellCssStyles(grid.getCellCssStyles(key));

        const self = this;

        function storeCellCssStyles(this: void, hash: any) {
            hashById = {};
            for (const row in hash) {
                const id: any = (self.rows[row as any] as any)[self.idProperty];
                hashById[id] = hash[row];
            }
        }

        function update(this: void) {
            if (hashById) {
                inHandler = true;
                self.ensureRowsByIdCache();
                const newHash: Record<number, any> = {};
                for (const id in hashById) {
                    const row = self.rowsById[id];
                    if (row != undefined) {
                        newHash[row] = hashById[id];
                    }
                }
                grid.setCellCssStyles(key, newHash);
                inHandler = false;
            }
        }

        const subFunc = function (this: void, e: any, args: any) {
            if (inHandler) { return; }
            if (key != args.key) { return; }
            if (args.hash) {
                storeCellCssStyles(args.hash);
            }
            else {
                grid.onCellCssStylesChanged.unsubscribe(subFunc);
                self.onRowsOrCountChanged.unsubscribe(update);
            }
        };

        grid.onCellCssStylesChanged.subscribe(subFunc);
        this.onRowsOrCountChanged.subscribe(update);
    }

    public addData(data: any) {

        if (this.onProcessData && data)
            data = this.onProcessData(data, this) || data;

        this.errorMessage = null;
        this.loading && typeof this.loading !== "boolean" && this.loading.abort();
        this.loading = false;

        if (!data) {
            this.errorMessage = this.errormsg ?? PagerTexts.DefaultLoadError;
            this.onPagingInfoChanged.notify({
                dataView: this,
                pagingInfo: this.getPagingInfo()
            });
            return false;
        }

        data.TotalCount = data.TotalCount || 0;
        data.Entities = data.Entities || [];

        if (!data.Skip || (!this.rowsPerPage && !data.Take))
            data.Page = 1;
        else
            data.Page = Math.ceil(data.Skip / (data.Take || this.rowsPerPage)) + 1;

        this.page = data.Page;
        this.totalCount = data.TotalCount;

        this.setItems(data.Entities);

        this.onPagingInfoChanged.notify({ pagingInfo: this.getPagingInfo(), dataView: this });
    }

    public populate() {
        if (this.populateLocks > 0) {
            this.populateCalls++;
            return;
        }

        this.populateCalls = 0;

        this.loading && typeof this.loading !== "boolean" && this.loading.abort();

        if (this.onSubmit) {
            const gh = this.onSubmit(this);
            if (gh === false)
                return false;
        }

        this.onDataLoading.notify({ dataView: this });

        if (!this.url)
            return false;

        // set loading event

        if (!this.seekToPage)
            this.seekToPage = 1;

        let request: ListRequest = {};

        const skip = (this.seekToPage - 1) * this.rowsPerPage;
        if (skip)
            request.Skip = skip;
        if (this.rowsPerPage)
            request.Take = this.rowsPerPage;

        if (this.sortBy && this.sortBy.length) {
            if (typeof this.sortBy !== "string")
                request.Sort = this.sortBy;
            else {
                request.Sort = [this.sortBy];
            }
        }

        if (this.params) {
            request = Object.assign(request, this.params);
        }

        const controller = new AbortController();

        const self = this;
        const serviceOptions: ServiceOptions<ListResponse<TItem>> = {
            allowRedirect: false,
            cache: "no-store",
            method: this.method,
            headers: {
                "Content-Type": this.contentType,
                "Accept": (this.dataType == "json" || this.dataType == "application/json") ? "application/json" : this.dataType
            },
            request,
            url: this.url,
            signal: controller.signal,
            errorMode: 'notification',
            onSuccess: function (this: void, response: ServiceResponse) {
                self.addData(response);
            },
            onCleanup: function (this: void) {
                self.loading = false;
                self.onPagingInfoChanged.notify({ pagingInfo: self.getPagingInfo(), dataView: self });
                self.onDataLoaded.notify({ dataView: self });
            }
        }

        if (this.onAjaxCall) {
            const ah = this.onAjaxCall(this, serviceOptions);
            if (ah === false) {
                this.loading = false;
                this.onPagingInfoChanged.notify({ pagingInfo: this.getPagingInfo(), dataView: this });
                return false;
            }
        }

        serviceCall(serviceOptions);
        this.onPagingInfoChanged.notify({ pagingInfo: this.getPagingInfo(), dataView: this });
        this.loading = controller;
    }

    public populateLock() {
        if (this.populateLocks == 0)
            this.populateCalls = 0;
        this.populateLocks++;
    }

    public populateUnlock() {
        if (this.populateLocks > 0) {
            this.populateLocks--;
            if (this.populateLocks == 0 && this.populateCalls > 0)
                this.populate();
        }
    }

    public getGroupItemMetadataProvider() {
        return this.groupItemMetadataProvider;
    }

    public setGroupItemMetadataProvider(value: GroupItemMetadataProvider) {
        this.groupItemMetadataProvider = value;
    }

    public getItemMetadataCallback(): (item: TItem, row: number) => ItemMetadata<TItem> | undefined {
        return this.itemMetadataCallback;
    }

    public setItemMetadataCallback(value: (item: TItem, row: number) => ItemMetadata<TItem>) {
        this.itemMetadataCallback = value;
    }

    /** @deprecated Gets the ID property name, for compatibility */
    get idField(): string {
        return this.idProperty;
    }

    private formatGroupValue = (ctx: FormatterContext<Group<TItem>>) => {
        // note that grid calls the format function provided via getGroupRowMetadata
        // so the ctx.item is always a Group and value of the group is in item.value, not ctx.value
        // as ctx.value is set by the grid to ctx.item["__groupdisplaycolumnfield__"],
        // so never use or rely on ctx.value here!        
        if (!ctx.item || ctx.item.level == null) {
            return ctx.escape(ctx.item?.value);
        }

        const gi = this.groupingInfos[ctx.item.level];
        if (!gi) {
            return ctx.escape(ctx.item?.value);
        }

        if (!gi.format && gi.formatter) {
            const formatter = gi.formatter;
            gi.format = convertCompatFormatter((_1, _2, _3, _4, item) => formatter(item));
        }

        if (gi.format) {
            return gi.format(ctx);
        } else {
            return ctx.escape(ctx.item?.value);
        }
    }
}

/**
 * Options for configuring a RemoteView instance
 */
export interface RemoteViewOptions<TItem = any> {
    /** Automatically load data (call populate) on initialization */
    autoLoad?: boolean;
    /** HTTP content type for service requests */
    contentType?: string;
    /** Expected data type of the service response */
    dataType?: string;
    /** Error message to display when requests fail */
    errormsg?: string;
    /** Filter criteria or function to apply to the data */
    filter?: RemoteViewFilter<TItem>;
    /** Callback function to get metadata for individual items */
    getItemMetadata?: (item: TItem, row: number) => ItemMetadata<TItem>;
    /** Provider for group item metadata in grouped views */
    groupItemMetadataProvider?: GroupItemMetadataProvider;
    /** Name of the field containing unique item identifiers */
    idField?: string;
    /** Whether to perform sorting locally instead of server-side */
    localSort?: boolean;
    /** HTTP method to use for service requests */
    method?: string;
    /** Callback function invoked before AJAX calls are made */
    onAjaxCall?: RemoteViewAjaxCallback<TItem>;
    /** Callback function to process data received from the server */
    onProcessData?: RemoteViewProcessCallback<TItem>;
    /** Callback function invoked before submitting service requests */
    onSubmit?: CancellableViewCallback<TItem>;
    /** Additional parameters to include in service requests */
    params?: Record<string, object>;
    /** Number of rows to display per page (0 for no paging) */
    rowsPerPage?: number;
    /** Initial page number to seek to on first load */
    seekToPage?: number;
    /** Initial sort criteria for the data */
    sortBy?: string | string[];
    /** URL of the service endpoint for data requests */
    url?: string;
}

const groupingDelimiter = ':|:';